/* ==========================================================================
   Gorgeous Living — Swatch overflow drawer

   Handles three tricky problems:
   1. Foxtheme's <variant-selects> re-renders its DOM when a variant is
      selected. Our wrapper div is recreated (via section reload) or our
      data-attributes are wiped. Solution: observe mutations inside
      variant-selects and re-apply our state after each mutation.
   2. Products with multiple options (size + color) need per-option tracking.
   3. Drawer swatches must bridge to theme radios and stay consistent.
   ========================================================================== */

(function () {
  'use strict';

  const STATE = new WeakMap();

  class GLSwatchWrap {
    constructor(wrap) {
      this.wrap = wrap;
      this.visibleCount = parseInt(wrap.dataset.visibleCount, 10) || 6;
      this.fieldset = wrap.querySelector('.gl-swatch-fieldset');
      this.moreBtn = wrap.querySelector('[data-gl-open-drawer]');
      this.drawer = wrap.querySelector('[data-gl-drawer]');
      this.backdrop = wrap.querySelector('[data-gl-backdrop]');

      if (!this.fieldset || !this.drawer) return;

      // Mark this wrap as initialized so we don't double-bind
      wrap.dataset.glInit = 'true';

      this.hideOverflow();
      this.bindEvents();
      this.syncDrawerSelection();
      this.observeTheme();
    }

    /**
     * Watch for DOM changes inside <variant-selects>. When the theme
     * re-renders radios (which it does when you select a variant), our
     * data-gl-overflow-hidden attributes are wiped. Re-apply them.
     */
    observeTheme() {
      const variantSelects = this.wrap.closest('variant-selects');
      if (!variantSelects) return;

      this.observer = new MutationObserver((mutations) => {
        // Debounce: batch all mutations in the current tick
        if (this._observerPending) return;
        this._observerPending = true;
        Promise.resolve().then(() => {
          this._observerPending = false;

          // Re-verify fieldset still exists (theme might have replaced it)
          const currentFieldset = this.wrap.querySelector('.gl-swatch-fieldset');
          if (currentFieldset && currentFieldset !== this.fieldset) {
            this.fieldset = currentFieldset;
          }

          if (!this.fieldset) return;

          // Re-apply overflow hiding
          this.clearAllOverflowMarks();
          this.hideOverflow();
          this.syncDrawerSelection();
        });
      });

      this.observer.observe(variantSelects, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['checked', 'class'],
      });
    }

    clearAllOverflowMarks() {
      this.fieldset.querySelectorAll('[data-gl-overflow-hidden]').forEach((el) => {
        el.removeAttribute('data-gl-overflow-hidden');
      });
    }

    /**
     * Mark swatch elements past visibleCount as hidden.
     */
    hideOverflow() {
      const radios = this.fieldset.querySelectorAll('input[type="radio"]');
      radios.forEach((radio, idx) => {
        if (idx >= this.visibleCount) {
          this.markHidden(radio);
        }
      });

      // If the checked radio is in overflow, promote it and hide another instead
      const checkedRadio = this.fieldset.querySelector('input[type="radio"]:checked');
      if (checkedRadio && checkedRadio.hasAttribute('data-gl-overflow-hidden')) {
        this.unmarkHidden(checkedRadio);
        const stillVisible = Array.from(this.fieldset.querySelectorAll(
          'input[type="radio"]:not([data-gl-overflow-hidden])'
        ));
        if (stillVisible.length > this.visibleCount) {
          for (let i = stillVisible.length - 1; i >= 0; i--) {
            if (stillVisible[i] !== checkedRadio) {
              this.markHidden(stillVisible[i]);
              break;
            }
          }
        }
      }
    }

    markHidden(radio) {
      radio.setAttribute('data-gl-overflow-hidden', '');

      if (radio.id) {
        const label = this.fieldset.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
        if (label) {
          label.setAttribute('data-gl-overflow-hidden', '');
          return;
        }
      }

      let sibling = radio.nextElementSibling;
      while (sibling) {
        if (sibling.tagName === 'LABEL') {
          sibling.setAttribute('data-gl-overflow-hidden', '');
          return;
        }
        if (sibling.tagName === 'INPUT') break;
        sibling = sibling.nextElementSibling;
      }
    }

    unmarkHidden(radio) {
      radio.removeAttribute('data-gl-overflow-hidden');

      if (radio.id) {
        const label = this.fieldset.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
        if (label) label.removeAttribute('data-gl-overflow-hidden');
      }

      let sibling = radio.nextElementSibling;
      while (sibling) {
        if (sibling.tagName === 'LABEL') {
          sibling.removeAttribute('data-gl-overflow-hidden');
          break;
        }
        if (sibling.tagName === 'INPUT') break;
        sibling = sibling.nextElementSibling;
      }
    }

    bindEvents() {
      if (this.moreBtn) {
        this.moreBtn.addEventListener('click', () => this.openDrawer());
      }

      const closeBtn = this.drawer.querySelector('[data-gl-close-drawer]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.closeDrawer());
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.closeDrawer());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.closeDrawer();
      });

      this.drawer.addEventListener('click', (e) => {
        const swatch = e.target.closest('[data-gl-swatch]');
        if (!swatch) return;
        this.selectValue(swatch.dataset.value);
      });
    }

    selectValue(value) {
      // Close drawer first for snappy feel
      this.closeDrawer();

      // Find and click the matching theme radio
      // We need to re-query because the theme may have re-rendered
      const radios = this.fieldset.querySelectorAll('input[type="radio"]');
      for (const radio of radios) {
        if (radio.value === value) {
          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          // The MutationObserver will handle re-hiding overflow after theme re-renders
          return;
        }
      }
    }

    syncDrawerSelection() {
      const checked = this.fieldset.querySelector('input[type="radio"]:checked');
      if (!checked) return;
      const value = checked.value;

      const swatches = this.drawer.querySelectorAll('[data-gl-swatch]');
      swatches.forEach((s) => {
        const match = s.dataset.value === value;
        s.classList.toggle('is-selected', match);
        s.setAttribute('aria-pressed', match ? 'true' : 'false');
      });
    }

    openDrawer() {
      this.syncDrawerSelection();

      this.backdrop.hidden = false;
      this.drawer.hidden = false;
      void this.drawer.offsetWidth;
      this.backdrop.setAttribute('data-open', '');
      this.drawer.setAttribute('data-open', '');
      document.body.classList.add('gl-drawer-open');

      setTimeout(() => {
        const closeBtn = this.drawer.querySelector('[data-gl-close-drawer]');
        if (closeBtn) closeBtn.focus();
      }, 100);
    }

    closeDrawer() {
      this.backdrop.removeAttribute('data-open');
      this.drawer.removeAttribute('data-open');
      document.body.classList.remove('gl-drawer-open');

      setTimeout(() => {
        this.backdrop.hidden = true;
        this.drawer.hidden = true;
      }, 400);

      if (this.moreBtn) this.moreBtn.focus();
    }

    isOpen() {
      return this.drawer.hasAttribute('data-open');
    }

    destroy() {
      if (this.observer) this.observer.disconnect();
    }
  }

  function init() {
    document.querySelectorAll('[data-gl-swatch-wrap]').forEach((wrap) => {
      // Skip wraps that already have a live instance attached
      if (wrap.dataset.glInit === 'true' && STATE.has(wrap)) return;

      // Clean up any dead instance (e.g. if theme replaced the wrap element)
      if (STATE.has(wrap)) {
        STATE.get(wrap).destroy();
        STATE.delete(wrap);
      }

      const instance = new GLSwatchWrap(wrap);
      STATE.set(wrap, instance);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reinit on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', init);

  // Reinit whenever the DOM changes in ways that might have added new wraps.
  // Useful for AJAX-updated product info sections.
  const globalObserver = new MutationObserver(() => {
    const wraps = document.querySelectorAll('[data-gl-swatch-wrap]:not([data-gl-init="true"])');
    if (wraps.length > 0) init();
  });

  if (document.body) {
    globalObserver.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      globalObserver.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
